import { Translate } from '@chipinside/translate'
import { FormHelperText } from '@mui/material'
import { styled } from '@mui/material/styles'
import { isNil } from 'lodash'
import { useEffect, useState } from 'react'

import NumberField from '#/components/BaseField/number'
import { isValidLatitude, isValidLongitude } from '#/components/FormCall/Inputs/PinMap/coordinates'
import { Maps } from '#/components/Maps'
import { useFarmLocation } from '#/store/ducks/farm'
import Fonts from '#/styles/Fonts'
import { numberDisplayDefaultOptions } from '#/utils/numberDisplay'

const Wrapper = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
})

const StyledDescription = styled('span')({
  fontSize: Fonts.fontSize.S,
  fontWeight: Fonts.fontWeight.medium,
  marginTop: 15,
})

const Inputs = styled('div')({
  '& > div': {
    flex: 1,
  },
  'display': 'flex',
  'gap': 10,
})

export default function PinMapInput({ description, disabled, error, onChange, required, value }) {
  const { latitude, longitude } = useFarmLocation()

  const [lat, setLat] = useState()
  const [lng, setLng] = useState()
  const [helperText, setHelperText] = useState({ lat: null, lng: null })

  useEffect(() => {
    if (value) {
      setLat(value?.lat)
      setLng(value?.lng)
    } else if (latitude && longitude) {
      setLat(latitude)
      setLng(longitude)
    }
  }, [])

  // Function to handle changes in latitude or longitude
  const handleChange = (value, name) => {
    const handleFunction = name === 'lat' ? setLat : setLng
    handleFunction(value)
  }

  // Function to handle input changes in latitude or longitude text fields
  const handleChangeInput = (value, name) => {
    handleChange(value, name)

    setHelperText(prevState => ({
      ...prevState,
      [name]: name === 'lat' ? isValidLatitude(value) : isValidLongitude(value),
    }))
  }

  // Effect to notify the parent component of changes in latitude or longitude
  useEffect(() => {
    if (!isNil(lat) || !isNil(lng)) {
      onChange({
        lat: lat ? Number(lat) : null,
        lng: lng ? Number(lng) : null,
      })
    }
  }, [lat, lng])

  return (
    <Wrapper>
      {/* Input fields for latitude and longitude */}
      <Inputs>
        <NumberField
          name="lat"
          label={Translate({ messageKey: 'latitude' })}
          onChange={value => handleChangeInput(value, 'lat')}
          value={lat ?? null}
          required={required}
          disabled={disabled}
          error={helperText.lat}
          format={{
            ...numberDisplayDefaultOptions,
            useGrouping: false,
          }}
        />
        <NumberField
          name="lng"
          label={Translate({ messageKey: 'longitude' })}
          onChange={value => handleChangeInput(value, 'lng')}
          value={lng ?? null}
          required={required}
          disabled={disabled}
          error={helperText.lng}
          format={{
            ...numberDisplayDefaultOptions,
            useGrouping: false,
          }}
        />
      </Inputs>
      {/* Description of the input */}
      <StyledDescription>{description}</StyledDescription>
      {/* Map wrapper containing PinMap component */}
      <Maps
        id="pin-map"
        height={'350px'}
        center={{ lat: Number(lat), lng: Number(lng) }}
        zoom={17}
        markers={{
          pin: [
            {
              lat: Number(lat),
              lng: Number(lng),
              setLat: e => handleChange(e, 'lat'),
              setLng: e => handleChange(e, 'lng'),
            },
          ],
        }}
      />
      {error && <FormHelperText error={Boolean(error)}>{error}</FormHelperText>}
    </Wrapper>
  )
}
